import { CommonModule } from '@angular/common';
import { Component, computed, HostBinding, input, linkedSignal, ChangeDetectionStrategy } from '@angular/core';

import { MdwIcon } from '@spaces-ui/meadow/icons';
import {
  BadgeNumber_1,
  BadgeNumber_2,
  BadgeNumber_3,
  BadgeNumber_4,
  BadgeNumber_5,
  BadgeNumber_6,
  BadgeNumber_7,
  BadgeNumber_8,
  BadgeNumber_9,
  BadgeNumber_9plus,
  ExternalConnection_Fill,
  Status_Offline,
  Status_Offline_snoozed,
  Status_Online,
  Status_Online_snoozed,
  StatusGuest_Offline,
  StatusGuest_Offline_snoozed,
  StatusGuest_Online,
  StatusGuest_Online_snoozed,
  User_Fill,
} from '@spaces-ui/meadow/icons/svgs';

import { PIP_AVATAR_FALLBACK, pipAvatarSrcFor } from './pip-avatar';

@Component({
  selector: 'mdw-avatar',
  imports: [CommonModule, MdwIcon],
  templateUrl: './avatar.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './avatar.scss',
  host: { '[class.squircle]': 'squircle()' },
})
export class MdwAvatar {
  public src = input.required<string | undefined>();
  public srcVersion = input.required<number | undefined>();
  public name = input.required<string>();
  public color = input.required<number | undefined>(); // value between 1 and 8
  public loadingUser = input.required<boolean>();

  public isGuest = input<boolean>();
  public alt = input<string>();
  public size = input<
    'small' | 'medium' | 'large' | 'x-large' | 'jumbo' | 'huge' | 'upload-preview'
  >('medium');
  public isDeleted = input<boolean>();
  /**
   * Brand/catalog SVG rendered in the avatar slot instead of a photo or initials
   * (integration bots whose identity is a UI-side icon). Loses to isDeleted, wins
   * over src.
   */
  public icon = input<string | undefined>();
  /** Rounded-square mask used for app/bot identities; bound as a host class. */
  public squircle = input<boolean>(false);
  public isPip = input<boolean>();
  /** Theme family whose pip portrait to show when isPip is set (see pipAvatarSrcFor). */
  public pipFamily = input<string>('magpie');

  // status
  public online = input<boolean>();
  public offline = input<boolean>();
  public snoozed = input<boolean>();
  public numberBadge = input<number>();
  public showConnectionIcon = input<boolean>();

  /**
   * Pip's portrait matches the bound theme family (each "bird" theme ships its
   * own avatar under `assets/images/pip-avatars/`). Writable so a missing asset
   * (e.g. a stale persisted family) can fall back; re-derives on family change.
   */
  public pipAvatarSrc = linkedSignal(() => pipAvatarSrcFor(this.pipFamily()));

  public onPipAvatarError(): void {
    this.pipAvatarSrc.set(PIP_AVATAR_FALLBACK);
  }

  public srcWithVersion = computed(() => {
    const src = this.src();
    const version = this.srcVersion();

    const srcIncludesVersion =
      src?.includes('?version=') ||
      src?.includes('&version=') ||
      src?.includes('?v=') ||
      src?.includes('&v=');

    if (srcIncludesVersion) {
      return src;
    }

    if (!src || version === undefined) {
      return undefined;
    }

    return `${src}?v=${version}`;
  });

  public icons = {
    Status_Online,
    Status_Offline,
    Status_Online_snoozed,
    Status_Offline_snoozed,
    StatusGuest_Online,
    StatusGuest_Offline,
    StatusGuest_Online_snoozed,
    StatusGuest_Offline_snoozed,
    BadgeNumber_1,
    BadgeNumber_2,
    BadgeNumber_3,
    BadgeNumber_4,
    BadgeNumber_5,
    BadgeNumber_6,
    BadgeNumber_7,
    BadgeNumber_8,
    BadgeNumber_9,
    BadgeNumber_9plus,
    User_Fill,
    ExternalConnection_Fill,
  };

  @HostBinding('class')
  public get sizeClass(): string {
    return `${this.size()}-avatar`;
  }
}
